/* eslint-disable max-lines -- Pre-existing violation */
import {
  Button,
  Checkbox,
  Dialog,
  Form,
  FormButton,
  FormField,
  openConfirmDialog,
  Separator,
  Table,
  TableRow,
  useFormContext,
  type FormControl,
} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useRef, useState} from 'react';
import {useFormState, useWatch} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useNewVehicleCatalogueLabels} from '@dms/features/new-vehicle';
import {
  DriveSelect,
  FuelTypeSelect,
  TransmissionSelect,
  useVehicleCatalogue,
  VehicleStyleSelect,
} from '@dms/shared';

import {HEX_COLOUR} from '../../components/ColourSwatch/getSwatchColor';
import {buildRemovalWarning} from './buildRemovalWarning';
import {
  Cell,
  ColourSwatch,
  column,
  dateWindowRefinement,
  HYBRID_FUEL,
  joinParts,
  optionalHexColour,
  optionalIsoDate,
  optionalNonNegativeIntInput,
  optionalPowerInput,
  optionalText,
  PASSENGER_CAR,
  powerInput,
  t,
} from './catalogueUi';
import {DraftRecordList, type DraftRecordListRow} from './DraftRecordList';
import {GrossPriceField} from './GrossPriceField';
import {optionalMoneyInput, priceInput} from './moneyInput';
import {NegativeMarginWarning} from './NegativeMarginWarning';
import {
  draftCodeConflict,
  removeDraftRecord,
  variantKey,
  type NewModelDraft,
} from './newModelDraft';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';
import {PriceWithGross} from './PriceWithGross';
import {useCatalogueVatRate} from './useCatalogueVatRate';

const HEX_PLACEHOLDER = '#1A2B3C';

const engineDialogSchema = z.object({
  name: z.string().trim().min(1),
  fuel: z.string().trim().min(1),
  hybridType: optionalText,
  powerKw: powerInput,
  powerPs: optionalPowerInput,
  displacementCcm: optionalNonNegativeIntInput,
  transmission: z.string().trim().min(1),
  drive: z.string().trim().min(1),
  factoryCode: optionalText,
  transmissionCode: optionalText,
});
type EngineDialogInput = z.input<typeof engineDialogSchema>;
type EngineDialogValues = z.output<typeof engineDialogSchema>;

const trimVariantSchema = z.object({
  engineId: z.string(),
  orderCode: optionalText,
  priceNet: priceInput,
  costNet: optionalMoneyInput,
});
const trimDialogSchema = z
  .object({
    name: z.string().trim().min(1),
    bodyStyle: z.string().trim().min(1),
    type: optionalText,
    factoryCode: optionalText,
    basePriceNet: priceInput,
    costNet: optionalMoneyInput,
    validFrom: optionalIsoDate,
    validTo: optionalIsoDate,
    engineIds: z.array(z.string()).min(1),
    variants: z.array(trimVariantSchema),
  })
  .superRefine(dateWindowRefinement('validFrom', 'validTo'))
  // `variants` holds a row for every draft engine; only selected engines need an order code.
  .superRefine((values, ctx) =>
    values.variants.forEach((variant, index) => {
      if (values.engineIds.includes(variant.engineId) && !variant.orderCode.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variants', index, 'orderCode'],
          message: t('entity.newVehicle.authoring.requireOrderCode', 'Enter the order code.'),
        });
      }
    })
  );
type TrimDialogInput = z.input<typeof trimDialogSchema>;
type TrimDialogValues = z.output<typeof trimDialogSchema>;

const colourDialogSchema = z.object({
  name: z.string().trim().min(1),
  kind: optionalText,
  factoryCode: optionalText,
  hex: optionalHexColour,
  priceNet: priceInput,
  costNet: optionalMoneyInput,
  trimIds: z.array(z.string()).min(1),
});
type ColourDialogInput = z.input<typeof colourDialogSchema>;
type ColourDialogValues = z.output<typeof colourDialogSchema>;

const upholsteryDialogSchema = z.object({
  name: z.string().trim().min(1),
  fabric: optionalText,
  factoryCode: optionalText,
  hex: optionalHexColour,
  priceNet: priceInput,
  costNet: optionalMoneyInput,
  trimIds: z.array(z.string()).min(1),
});
type UpholsteryDialogInput = z.input<typeof upholsteryDialogSchema>;
type UpholsteryDialogValues = z.output<typeof upholsteryDialogSchema>;

type EditorState = {kind: 'engine' | 'trim' | 'colour' | 'upholstery'; id?: string};
type CodeConflict = ReturnType<typeof draftCodeConflict>;

type NewModelCollectionStepsProps = {
  step: 4 | 5 | 6 | 7;
  draft: NewModelDraft;
  canViewCost: boolean;
  currency: string;
  isLocked: boolean;
  update: (draft: NewModelDraft) => void;
};

const trimCheckboxOptions = (draft: NewModelDraft) =>
  draft.trims.map((trim) => ({
    value: trim.id,
    label: trim.name || t('entity.newVehicle.authoring.unnamedTrim', 'Unnamed trim'),
  }));

const engineCheckboxOptions = (draft: NewModelDraft) =>
  draft.engines.map((engine) => ({
    value: engine.id,
    label: engine.name || t('entity.newVehicle.authoring.unnamedEngine', 'Unnamed engine'),
  }));

function DialogActions<T extends Record<string, unknown>>(props: {
  control: FormControl<T>;
  isEditing: boolean;
  addLabel: string;
  onCancel: () => void;
}) {
  return (
    <HStack justify="flex-end" spacing={3}>
      <Button
        variant="secondary"
        title={t('general.actions.cancel', 'Cancel')}
        onClick={props.onCancel}
      />
      <FormButton
        control={props.control}
        type="submit"
        title={props.isEditing ? t('general.actions.saveChanges', 'Save changes') : props.addLabel}
      />
    </HStack>
  );
}

/**
 * The backend's refusal of a code, shown on its field until the author edits the code. It derives
 * from the field's dirty state in the form, so there is no copy to keep in sync.
 */
function useCodeConflictMessage(name: string, message: string | undefined) {
  const {getFieldState} = useFormContext();
  const formState = useFormState({name});
  return getFieldState(name, formState).isDirty ? undefined : message;
}

function EngineFactoryCodeField(props: {
  control: FormControl<EngineDialogInput>;
  codeConflict?: string;
}) {
  return (
    <FormField
      control={props.control}
      name="factoryCode"
      type="text"
      label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
      errorMessage={useCodeConflictMessage('factoryCode', props.codeConflict)}
    />
  );
}

/** Fuel, plus the hybrid type a hybrid fuel carries; switching away from hybrid clears it. */
function EngineFuelFields(props: {control: FormControl<EngineDialogInput>}) {
  const form = useFormContext<EngineDialogInput>();
  const fuel = useWatch({control: props.control, name: 'fuel'});
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  return (
    <>
      <FuelTypeSelect
        vehicleType={PASSENGER_CAR}
        control={props.control}
        name="fuel"
        label={t('entity.newVehicle.catalogue.fuel', 'Fuel')}
        isMenuInPortal
        isNotClearable
        isRequired
        onChange={(value) => {
          if (value !== HYBRID_FUEL) form.setValue('hybridType', ''); // eslint-disable-line curly -- Pre-existing violation
        }}
        data-testid="new-model-wizard-engine"
      />
      <Show when={fuel === HYBRID_FUEL}>
        <FormField
          control={props.control}
          name="hybridType"
          type="choice"
          menuInPortal
          label={t('general.labels.hybridType', 'Hybrid type')}
          options={catalogue.hybridTypeOptions ?? []}
          isLoading={isLoading}
          isDisabled={isNilOrEmpty(catalogue.hybridTypeOptions)}
        />
      </Show>
    </>
  );
}

function EngineDialog(props: {
  engine?: NewModelDraft['engines'][number];
  codeConflict?: string;
  onClose: () => void;
  onSave: (values: EngineDialogValues) => Promise<void>;
}) {
  const isEditing = props.engine !== undefined;
  return (
    <Dialog
      isOpen
      title={
        isEditing
          ? t('entity.newVehicle.authoring.editEngine', 'Edit engine')
          : t('entity.newVehicle.catalogue.addEngine', 'Add engine')
      }
      onClose={props.onClose}
    >
      <Form<EngineDialogInput>
        experimentalZodSchema={engineDialogSchema}
        defaultValues={{
          name: props.engine?.name ?? '',
          fuel: props.engine?.fuel ?? '',
          hybridType: props.engine?.hybridType ?? '',
          powerKw: props.engine?.powerKw ?? '',
          powerPs: props.engine?.powerPs ?? '',
          displacementCcm: props.engine?.displacementCcm ?? '',
          transmission: props.engine?.transmission ?? '',
          drive: props.engine?.drive ?? '',
          factoryCode: props.engine?.factoryCode ?? '',
          transmissionCode: props.engine?.transmissionCode ?? '',
        }}
        onSubmit={(values) => {
          const parsed = engineDialogSchema.parse(values);
          return props.onSave({
            ...parsed,
            hybridType: parsed.fuel === HYBRID_FUEL ? parsed.hybridType : '',
          });
        }}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={[1, 1, 2]} spacing={4}>
              <FormField
                control={control}
                name="name"
                type="text"
                label={t('general.labels.name', 'Name')}
                isRequired
              />
              <EngineFuelFields control={control} />
              <NumberField
                control={control}
                name="powerKw"
                label={t('entity.newVehicle.catalogue.powerKw', 'Power (kW)')}
                isRequired
              />
              <NumberField
                control={control}
                name="powerPs"
                label={t('entity.newVehicle.catalogue.powerPs', 'Power (PS)')}
              />
              <NumberField
                control={control}
                name="displacementCcm"
                label={t('entity.newVehicle.catalogue.displacementCcm', 'Displacement (ccm)')}
              />
              <TransmissionSelect
                vehicleType={PASSENGER_CAR}
                control={control}
                name="transmission"
                label={t('entity.newVehicle.catalogue.transmission', 'Transmission')}
                isMenuInPortal
                isNotClearable
                isRequired
                data-testid="new-model-wizard-engine"
              />
              <DriveSelect
                vehicleType={PASSENGER_CAR}
                control={control}
                name="drive"
                label={t('entity.newVehicle.catalogue.drive', 'Drive')}
                isMenuInPortal
                isNotClearable
                isRequired
                data-testid="new-model-wizard-engine"
              />
              <EngineFactoryCodeField control={control} codeConflict={props.codeConflict} />
              <FormField
                control={control}
                name="transmissionCode"
                type="text"
                label={t('entity.newVehicle.authoring.transmissionCode', 'Transmission code')}
              />
            </Grid>
            <DialogActions
              control={control}
              isEditing={isEditing}
              addLabel={t('entity.newVehicle.catalogue.addEngine', 'Add engine')}
              onCancel={props.onClose}
            />
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}

function TrimDialogFields(props: {
  control: FormControl<TrimDialogInput>;
  draft: NewModelDraft;
  canViewCost: boolean;
  currency: string;
  /**
   * The trim exists in the catalogue already; only the variant whose order code the backend refused
   * can change its code or be unticked.
   */
  isLocked: boolean;
  trimId?: string;
  codeConflict: CodeConflict;
}) {
  const form = useFormContext<TrimDialogValues>();
  const vatRate = useCatalogueVatRate();
  const isConflicted = (engineId: string) =>
    props.codeConflict?.rowId === variantKey(props.trimId ?? '', engineId);
  const conflictIndex = props.draft.engines.findIndex(({id}) => isConflicted(id));
  const codeConflictMessage = useCodeConflictMessage(
    `variants.${conflictIndex}.orderCode`,
    conflictIndex === -1 ? undefined : props.codeConflict?.message
  );
  const selectedEngineIds = useWatch({control: props.control, name: 'engineIds'});
  const basePriceNet = useWatch({control: props.control, name: 'basePriceNet'});
  const previousBasePriceNet = useRef(basePriceNet);
  const updateDefaultVariantPrices = (value: string | null) => {
    const nextValue = value ?? '';
    form.getValues('variants').forEach((variant, index) => {
      if (variant.priceNet === previousBasePriceNet.current) {
        form.setValue(`variants.${index}.priceNet`, nextValue, {shouldDirty: true});
      }
    });
    previousBasePriceNet.current = nextValue;
  };
  return (
    <VStack spacing={4}>
      <Grid columns={[1, 1, 2]} spacing={4}>
        <FormField
          control={props.control}
          name="name"
          isDisabled={props.isLocked}
          type="text"
          label={t('general.labels.name', 'Name')}
          isRequired
        />
        <VehicleStyleSelect
          vehicleType={PASSENGER_CAR}
          control={props.control}
          name="bodyStyle"
          isDisabled={props.isLocked}
          label={t('entity.newVehicle.catalogue.bodyStyle', 'Body style')}
          isMenuInPortal
          isNotClearable
          isRequired
          data-testid="new-model-wizard-trim"
        />
        <FormField
          control={props.control}
          name="type"
          isDisabled={props.isLocked}
          type="text"
          label={t('entity.newVehicle.catalogue.trimType', 'Trim type')}
        />
        <FormField
          control={props.control}
          name="factoryCode"
          isDisabled={props.isLocked}
          type="text"
          label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
        />
        <PriceRow
          vatRate={vatRate}
          canViewCost={props.canViewCost}
          spacing={4}
          span={[1, 1, 2]}
          stackBelow="notebook"
        >
          <GrossPriceField
            control={props.control}
            name="basePriceNet"
            vatRate={vatRate}
            isDisabled={props.isLocked}
            label={t('entity.newVehicle.catalogue.basePriceNet', 'Base price net')}
            isRequired
            onChange={updateDefaultVariantPrices}
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              isDisabled={props.isLocked}
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
        <FormField
          control={props.control}
          name="validFrom"
          isDisabled={props.isLocked}
          type="apiDate"
          label={t('entity.newVehicle.authoring.validFrom', 'Valid from')}
        />
        <FormField
          control={props.control}
          name="validTo"
          isDisabled={props.isLocked}
          type="apiDate"
          label={t('entity.newVehicle.authoring.validTo', 'Valid to')}
        />
      </Grid>
      <NegativeMarginWarning
        control={props.control}
        priceName="basePriceNet"
        costName="costNet"
        currency={props.currency}
        canViewCost={props.canViewCost}
      />
      <FormField
        control={props.control}
        name="engineIds"
        type="checkboxes"
        direction="vertical"
        label={t('entity.newVehicle.authoring.availableEngines', 'Available engines')}
        options={engineCheckboxOptions(props.draft).map((option) => ({
          ...option,
          isDisabled: props.isLocked && !isConflicted(option.value),
        }))}
        isRequired
      />
      {props.draft.engines
        .map((engine, index) => ({engine, index}))
        .filter(({engine}) => selectedEngineIds.includes(engine.id))
        .map(({engine, index}, selectedIndex) => (
          <VStack key={engine.id} spacing={3}>
            <Show when={selectedIndex > 0}>
              <Separator spacing={0} />
            </Show>
            <Text alternative>{engine.name}</Text>
            <PriceRow
              vatRate={vatRate}
              canViewCost={props.canViewCost}
              spacing={3}
              leadingCells={1}
              stackBelow="tablet"
            >
              <FormField
                control={props.control}
                name={`variants.${index}.orderCode`}
                type="text"
                label={t('entity.newVehicle.catalogue.orderCode', 'Order code')}
                isRequired
                isDisabled={props.isLocked && !isConflicted(engine.id)}
                errorMessage={isConflicted(engine.id) ? codeConflictMessage : undefined}
              />
              <GrossPriceField
                control={props.control}
                name={`variants.${index}.priceNet`}
                vatRate={vatRate}
                isDisabled={props.isLocked}
                label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
                isRequired
              />
              <Show when={props.canViewCost}>
                <NumberField
                  control={props.control}
                  name={`variants.${index}.costNet`}
                  isDisabled={props.isLocked}
                  label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                />
              </Show>
            </PriceRow>
            <NegativeMarginWarning
              control={props.control}
              priceName={`variants.${index}.priceNet`}
              costName={`variants.${index}.costNet`}
              currency={props.currency}
              canViewCost={props.canViewCost}
            />
          </VStack>
        ))}
    </VStack>
  );
}

function TrimDialog(props: {
  trim?: NewModelDraft['trims'][number];
  draft: NewModelDraft;
  canViewCost: boolean;
  currency: string;
  isLocked: boolean;
  codeConflict: CodeConflict;
  onClose: () => void;
  onSave: (values: TrimDialogValues) => Promise<void>;
}) {
  const isEditing = props.trim !== undefined;
  const currentVariants = props.trim
    ? props.draft.variants.filter((variant) => variant.trimId === props.trim?.id)
    : [];
  return (
    <Dialog
      isOpen
      size="large"
      title={
        isEditing
          ? t('entity.newVehicle.authoring.editTrim', 'Edit trim level')
          : t('entity.newVehicle.catalogue.addTrimLevel', 'Add trim level')
      }
      onClose={props.onClose}
    >
      <Form<TrimDialogInput>
        experimentalZodSchema={trimDialogSchema}
        defaultValues={{
          name: props.trim?.name ?? '',
          bodyStyle: props.trim?.bodyStyle ?? '',
          type: props.trim?.type ?? '',
          factoryCode: props.trim?.factoryCode ?? '',
          basePriceNet: props.trim?.basePriceNet ?? '0.00',
          costNet: props.trim?.costNet ?? '',
          validFrom: props.trim?.validFrom ?? null,
          validTo: props.trim?.validTo ?? null,
          engineIds: currentVariants.map((variant) => variant.engineId),
          variants: props.draft.engines.map((engine) => {
            const variant = currentVariants.find((row) => row.engineId === engine.id);
            return {
              engineId: engine.id,
              orderCode: variant?.orderCode ?? '',
              priceNet: variant?.priceNet ?? props.trim?.basePriceNet ?? '0.00',
              costNet: variant?.costNet ?? '',
            };
          }),
        }}
        onSubmit={(values) => props.onSave(trimDialogSchema.parse(values))}
      >
        {(control) => (
          <VStack spacing={4}>
            <TrimDialogFields
              control={control}
              draft={props.draft}
              canViewCost={props.canViewCost}
              currency={props.currency}
              isLocked={props.isLocked}
              trimId={props.trim?.id}
              codeConflict={props.codeConflict}
            />
            <DialogActions
              control={control}
              isEditing={isEditing}
              addLabel={t('entity.newVehicle.catalogue.addTrimLevel', 'Add trim level')}
              onCancel={props.onClose}
            />
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}

/** Reads the dialog's `hex` field through the form context, so colour and upholstery share it. */
function HexSwatchPreview() {
  const hex = useWatch<{hex: string | null}>({name: 'hex'}) ?? '';
  return (
    <Show when={HEX_COLOUR.test(hex)}>
      {/* centers the 24px swatch against the 32px input row */}
      <Box paddingBottom={1}>
        <ColourSwatch hex={hex} />
      </Box>
    </Show>
  );
}

function ColourDialogFields(props: {
  control: FormControl<ColourDialogInput>;
  canViewCost: boolean;
  draft: NewModelDraft;
  codeConflict?: string;
}) {
  const codeConflictMessage = useCodeConflictMessage('factoryCode', props.codeConflict);
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const vatRate = useCatalogueVatRate();
  return (
    <VStack spacing={4}>
      <Grid columns={[1, 1, 2]} spacing={4}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          isRequired
        />
        <FormField
          control={props.control}
          name="kind"
          type="choice"
          menuInPortal
          label={t('entity.newVehicle.catalogue.kind', 'Kind')}
          options={catalogue.bodyColorTypeOptions ?? []}
          isLoading={isLoading}
          isDisabled={isNilOrEmpty(catalogue.bodyColorTypeOptions)}
        />
        <FormField
          control={props.control}
          name="factoryCode"
          type="text"
          label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          errorMessage={codeConflictMessage}
        />
        <HStack align="flex-end" spacing={3}>
          <Box width="100%">
            <FormField
              control={props.control}
              name="hex"
              type="text"
              label={t('entity.newVehicle.authoring.colourHex', 'Hex')}
              placeholder={HEX_PLACEHOLDER}
            />
          </Box>
          <HexSwatchPreview />
        </HStack>
        <PriceRow
          vatRate={vatRate}
          canViewCost={props.canViewCost}
          spacing={4}
          span={[1, 1, 2]}
          stackBelow="notebook"
        >
          <GrossPriceField
            control={props.control}
            name="priceNet"
            vatRate={vatRate}
            label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
            isRequired
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
      </Grid>
      <FormField
        control={props.control}
        name="trimIds"
        type="checkboxes"
        direction="vertical"
        label={t('entity.newVehicle.authoring.availableTrims', 'Available on trims')}
        options={trimCheckboxOptions(props.draft)}
        isRequired
      />
    </VStack>
  );
}

function ColourDialog(props: {
  colour?: NewModelDraft['colours'][number];
  draft: NewModelDraft;
  canViewCost: boolean;
  codeConflict?: string;
  onClose: () => void;
  onSave: (values: ColourDialogValues) => Promise<void>;
}) {
  const isEditing = props.colour !== undefined;
  return (
    <Dialog
      isOpen
      title={
        isEditing
          ? t('entity.newVehicle.authoring.editColour', 'Edit colour')
          : t('entity.newVehicle.authoring.addColour', 'Add colour')
      }
      onClose={props.onClose}
    >
      <Form<ColourDialogInput>
        experimentalZodSchema={colourDialogSchema}
        defaultValues={{
          name: props.colour?.name ?? '',
          kind: props.colour?.kind ?? '',
          factoryCode: props.colour?.factoryCode ?? '',
          hex: props.colour?.hex ?? '',
          priceNet: props.colour?.priceNet ?? '0.00',
          costNet: props.colour?.costNet ?? '',
          trimIds: props.colour?.trimIds ?? [],
        }}
        onSubmit={(values) => props.onSave(colourDialogSchema.parse(values))}
      >
        {(control) => (
          <VStack spacing={4}>
            <ColourDialogFields
              control={control}
              canViewCost={props.canViewCost}
              draft={props.draft}
              codeConflict={props.codeConflict}
            />
            <DialogActions
              control={control}
              isEditing={isEditing}
              addLabel={t('entity.newVehicle.authoring.addColour', 'Add colour')}
              onCancel={props.onClose}
            />
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}

function UpholsteryDialog(props: {
  upholstery?: NewModelDraft['upholsteries'][number];
  draft: NewModelDraft;
  canViewCost: boolean;
  onClose: () => void;
  onSave: (values: UpholsteryDialogValues) => Promise<void>;
}) {
  const isEditing = props.upholstery !== undefined;
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const vatRate = useCatalogueVatRate();
  return (
    <Dialog
      isOpen
      title={
        isEditing
          ? t('entity.newVehicle.authoring.editUpholstery', 'Edit upholstery')
          : t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')
      }
      onClose={props.onClose}
    >
      <Form<UpholsteryDialogInput>
        experimentalZodSchema={upholsteryDialogSchema}
        defaultValues={{
          name: props.upholstery?.name ?? '',
          fabric: props.upholstery?.fabric ?? '',
          factoryCode: props.upholstery?.factoryCode ?? '',
          hex: props.upholstery?.hex ?? '',
          priceNet: props.upholstery?.priceNet ?? '0.00',
          costNet: props.upholstery?.costNet ?? '',
          trimIds: props.upholstery?.trimIds ?? [],
        }}
        onSubmit={(values) => props.onSave(upholsteryDialogSchema.parse(values))}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={[1, 1, 2]} spacing={4}>
              <FormField
                control={control}
                name="name"
                type="text"
                label={t('general.labels.name', 'Name')}
                isRequired
              />
              <FormField
                control={control}
                name="fabric"
                type="choice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.fabricMaterial', 'Fabric / material')}
                options={catalogue.interiorMaterialOptions ?? []}
                isLoading={isLoading}
                isDisabled={isNilOrEmpty(catalogue.interiorMaterialOptions)}
              />
              <FormField
                control={control}
                name="factoryCode"
                type="text"
                label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
              />
              <HStack align="flex-end" spacing={3}>
                <Box width="100%">
                  <FormField
                    control={control}
                    name="hex"
                    type="text"
                    label={t('entity.newVehicle.authoring.colourHex', 'Hex')}
                    placeholder={HEX_PLACEHOLDER}
                  />
                </Box>
                <HexSwatchPreview />
              </HStack>
              <PriceRow
                vatRate={vatRate}
                canViewCost={props.canViewCost}
                spacing={4}
                span={[1, 1, 2]}
                stackBelow="notebook"
              >
                <GrossPriceField
                  control={control}
                  name="priceNet"
                  vatRate={vatRate}
                  label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
                  isRequired
                />
                <Show when={props.canViewCost}>
                  <NumberField
                    control={control}
                    name="costNet"
                    label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                  />
                </Show>
              </PriceRow>
            </Grid>
            <FormField
              control={control}
              name="trimIds"
              type="checkboxes"
              direction="vertical"
              label={t('entity.newVehicle.authoring.availableTrims', 'Available on trims')}
              options={trimCheckboxOptions(props.draft)}
              isRequired
            />
            <DialogActions
              control={control}
              isEditing={isEditing}
              addLabel={t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')}
              onCancel={props.onClose}
            />
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}

function CompatibilityMatrix(props: {
  draft: NewModelDraft;
  isLocked: boolean;
  update: (draft: NewModelDraft) => void;
}) {
  return (
    <Show when={props.draft.colours.length > 0 && props.draft.upholsteries.length > 0}>
      <VStack spacing={3}>
        <Separator spacing={0} />
        <Heading size={4}>
          {t('entity.newVehicle.authoring.compatibility', 'Colour × upholstery compatibility')}
        </Heading>
        <Text size="small" color="secondary">
          {t(
            'entity.newVehicle.authoring.compatibilityHint',
            "Untick a pair that can't be ordered together."
          )}
        </Text>
        <Box overflowX="auto" tabIndex={0}>
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.upholstery', 'Upholstery')),
              ...props.draft.colours.map((colour) =>
                column(
                  <HStack key={colour.id} align="center" justify="center" spacing={2}>
                    <ColourSwatch hex={colour.hex} />
                    <Text size="small">{colour.name}</Text>
                  </HStack>,
                  {width: 40, align: 'center'}
                )
              ),
            ]}
          >
            {props.draft.upholsteries.map((upholstery) => (
              <TableRow key={upholstery.id}>
                <Cell>{upholstery.name}</Cell>
                {props.draft.colours.map((colour) => {
                  const isCompatible = !props.draft.restrictions.some(
                    (row) => row.colourId === colour.id && row.upholsteryId === upholstery.id
                  );
                  const label = t(
                    'entity.newVehicle.authoring.compatibilityPair',
                    '{colour} × {upholstery}',
                    {colour: colour.name, upholstery: upholstery.name}
                  );
                  return (
                    <Cell key={colour.id} align="center">
                      <Checkbox
                        aria-label={label}
                        value={isCompatible}
                        isDisabled={props.isLocked}
                        onChange={(checked) =>
                          props.update({
                            ...props.draft,
                            restrictions: checked
                              ? props.draft.restrictions.filter(
                                  (row) =>
                                    row.colourId !== colour.id || row.upholsteryId !== upholstery.id
                                )
                              : [
                                  ...props.draft.restrictions,
                                  {colourId: colour.id, upholsteryId: upholstery.id},
                                ],
                          })
                        }
                      />
                    </Cell>
                  );
                })}
              </TableRow>
            ))}
          </Table>
        </Box>
      </VStack>
    </Show>
  );
}

export function NewModelCollectionSteps(props: NewModelCollectionStepsProps) {
  const [editor, setEditor] = useState<EditorState | null>(null);
  const closeEditor = () => setEditor(null);
  const labels = useNewVehicleCatalogueLabels();
  const vatRate = useCatalogueVatRate();
  const codeConflict = draftCodeConflict(props.draft);
  const rowError = (id: string) =>
    codeConflict?.recordId === id ? codeConflict.message : undefined;
  // An engine or colour whose create the backend refused; a refused variant is unticked on its trim.
  const isRefused = (id: string) => codeConflict?.rowId === id;
  const engine =
    editor?.kind === 'engine' ? props.draft.engines.find((row) => row.id === editor.id) : undefined;
  const trim =
    editor?.kind === 'trim' ? props.draft.trims.find((row) => row.id === editor.id) : undefined;
  const colour =
    editor?.kind === 'colour' ? props.draft.colours.find((row) => row.id === editor.id) : undefined;
  const upholstery =
    editor?.kind === 'upholstery'
      ? props.draft.upholsteries.find((row) => row.id === editor.id)
      : undefined;
  const engineRows: DraftRecordListRow[] = props.draft.engines.map((row) => ({
    id: row.id,
    name: row.name,
    // The factory code joins the spec line, as it does for colours and upholstery. It used to sit
    // in the trailing slot, which every other list uses for a price, so a bare code read as money.
    subtitle: joinParts([
      row.fuel && labels.formatFuel(row),
      row.powerKw && `${row.powerKw} kW`,
      row.transmission && labels.getTransmission(row.transmission),
      row.drive && labels.getDrive(row.drive),
      row.factoryCode,
    ]),
    error: rowError(row.id),
    isRemovable: isRefused(row.id),
  }));
  const trimRows: DraftRecordListRow[] = props.draft.trims.map((row) => ({
    id: row.id,
    name: row.name,
    subtitle: joinParts([
      row.bodyStyle && labels.getBodyStyle(row.bodyStyle),
      t('entity.newVehicle.authoring.enginesCount', '{count} engines', {
        count: props.draft.variants.filter((variant) => variant.trimId === row.id).length,
      }),
    ]),
    values: [
      <PriceWithGross
        key="price"
        amount={row.basePriceNet}
        currency={props.currency}
        vatRate={vatRate}
      />,
    ],
    error: rowError(row.id),
  }));
  const colourRows: DraftRecordListRow[] = props.draft.colours.map((row) => ({
    id: row.id,
    name: row.name,
    leading: <ColourSwatch hex={row.hex} />,
    subtitle: joinParts([row.kind && labels.getColorType(row.kind), row.factoryCode]),
    values: [
      <PriceWithGross
        key="price"
        amount={row.priceNet}
        currency={props.currency}
        vatRate={vatRate}
      />,
    ],
    error: rowError(row.id),
    isRemovable: isRefused(row.id),
  }));
  const upholsteryRows: DraftRecordListRow[] = props.draft.upholsteries.map((row) => ({
    id: row.id,
    name: row.name,
    leading: <ColourSwatch hex={row.hex} />,
    subtitle: joinParts([row.fabric && labels.getInteriorMaterial(row.fabric), row.factoryCode]),
    values: [
      <PriceWithGross
        key="price"
        amount={row.priceNet}
        currency={props.currency}
        vatRate={vatRate}
      />,
    ],
  }));
  const remove = (collection: 'engines' | 'trims' | 'colours' | 'upholsteries', id: string) => {
    const removeRecord = () => props.update(removeDraftRecord(props.draft, collection, id));
    const warning = buildRemovalWarning(props.draft, collection, id);
    const text = match(warning)
      .with({kind: 'engines'}, ({count}) =>
        t(
          'entity.newVehicle.confirmations.removeRecordWithVariants',
          'Removing this record also removes its variants from the variant matrix ({count}). Continue?',
          {count}
        )
      )
      .with({kind: 'trims'}, ({counts}) =>
        t(
          'entity.newVehicle.confirmations.removeTrimWithDependents',
          'Removing this trim level also removes its {variants} variants and unlinks it from {colours} colours, {upholsteries} upholstery options, {equipment} equipment items and {packages} packages. Continue?',
          counts
        )
      )
      .with({kind: 'trimLinked'}, ({counts}) =>
        t(
          'entity.newVehicle.confirmations.removeRecordWithTrimsAndRestrictions',
          'Removing this record also removes its availability on {trims} trim levels and {restrictions} restrictions. Continue?',
          counts
        )
      )
      .exhaustive();

    if (warning.count === 0) {
      removeRecord();
      return;
    }
    openConfirmDialog({text, onConfirm: removeRecord});
  };

  return (
    <VStack spacing={4}>
      <Show when={props.step === 4}>
        <DraftRecordList
          title={t('entity.newVehicle.catalogue.engines', 'Engines')}
          addLabel={t('entity.newVehicle.catalogue.addEngine', 'Add engine')}
          emptyHeadline={t('entity.newVehicle.authoring.noEngines', 'No engines yet')}
          emptySubheadline={t(
            'entity.newVehicle.authoring.noEnginesHint',
            'Add the engines available for this model year.'
          )}
          rows={engineRows}
          isLocked={props.isLocked}
          onAdd={() => setEditor({kind: 'engine'})}
          onEdit={(id) => setEditor({kind: 'engine', id})}
          onRemove={(id) => remove('engines', id)}
        />
      </Show>
      <Show when={props.step === 5}>
        <DraftRecordList
          title={t('entity.newVehicle.catalogue.trimLevels', 'Trim levels')}
          addLabel={t('entity.newVehicle.catalogue.addTrimLevel', 'Add trim level')}
          emptyHeadline={t('entity.newVehicle.authoring.noTrims', 'No trim levels yet')}
          emptySubheadline={t(
            'entity.newVehicle.authoring.noTrimsHint',
            'Add the trim levels dealers can offer for this model year.'
          )}
          rows={trimRows}
          isLocked={props.isLocked}
          onAdd={() => setEditor({kind: 'trim'})}
          onEdit={(id) => setEditor({kind: 'trim', id})}
          onRemove={(id) => remove('trims', id)}
        />
      </Show>
      <Show when={props.step === 6}>
        <DraftRecordList
          title={t('entity.newVehicle.authoring.colors', 'Colours')}
          addLabel={t('entity.newVehicle.authoring.addColour', 'Add colour')}
          emptyHeadline={t('entity.newVehicle.authoring.noColours', 'No colours yet')}
          emptySubheadline={t(
            'entity.newVehicle.authoring.noColoursHint',
            'Add the exterior colours available for this model year.'
          )}
          rows={colourRows}
          isLocked={props.isLocked}
          onAdd={() => setEditor({kind: 'colour'})}
          onEdit={(id) => setEditor({kind: 'colour', id})}
          onRemove={(id) => remove('colours', id)}
        />
      </Show>
      <Show when={props.step === 7}>
        <DraftRecordList
          title={t('entity.newVehicle.catalogue.upholstery', 'Upholstery')}
          addLabel={t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')}
          emptyHeadline={t('entity.newVehicle.authoring.noUpholstery', 'No upholstery yet')}
          emptySubheadline={t(
            'entity.newVehicle.authoring.noUpholsteryHint',
            'Add the upholstery choices available for this model year.'
          )}
          rows={upholsteryRows}
          isLocked={props.isLocked}
          onAdd={() => setEditor({kind: 'upholstery'})}
          onEdit={(id) => setEditor({kind: 'upholstery', id})}
          onRemove={(id) => remove('upholsteries', id)}
        />
        <CompatibilityMatrix draft={props.draft} isLocked={props.isLocked} update={props.update} />
      </Show>
      <Show when={editor?.kind === 'engine'}>
        <EngineDialog
          engine={engine}
          codeConflict={engine && rowError(engine.id)}
          onClose={closeEditor}
          onSave={(values) => {
            const id = engine?.id ?? crypto.randomUUID();
            props.update({
              ...props.draft,
              engines: engine
                ? props.draft.engines.map((row) => (row.id === id ? {...values, id} : row))
                : [...props.draft.engines, {...values, id}],
            });
            closeEditor();
            return Promise.resolve();
          }}
        />
      </Show>
      <Show when={editor?.kind === 'trim'}>
        <TrimDialog
          trim={trim}
          draft={props.draft}
          canViewCost={props.canViewCost}
          currency={props.currency}
          isLocked={props.isLocked}
          codeConflict={codeConflict}
          onClose={closeEditor}
          onSave={(values) => {
            const id = trim?.id ?? crypto.randomUUID();
            const nextTrim = {
              id,
              name: values.name,
              bodyStyle: values.bodyStyle,
              type: values.type,
              factoryCode: values.factoryCode,
              basePriceNet: values.basePriceNet,
              costNet: values.costNet,
              validFrom: values.validFrom,
              validTo: values.validTo,
            };
            const variants = values.variants
              .filter((variant) => values.engineIds.includes(variant.engineId))
              .map((variant) => ({
                id:
                  props.draft.variants.find(
                    (row) => row.trimId === id && row.engineId === variant.engineId
                  )?.id ?? crypto.randomUUID(),
                trimId: id,
                engineId: variant.engineId,
                orderCode: variant.orderCode,
                priceNet: variant.priceNet || values.basePriceNet,
                costNet: variant.costNet,
              }));
            props.update({
              ...props.draft,
              trims: trim
                ? props.draft.trims.map((row) => (row.id === id ? nextTrim : row))
                : [...props.draft.trims, nextTrim],
              variants: [...props.draft.variants.filter((row) => row.trimId !== id), ...variants],
            });
            closeEditor();
            return Promise.resolve();
          }}
        />
      </Show>
      <Show when={editor?.kind === 'colour'}>
        <ColourDialog
          colour={colour}
          draft={props.draft}
          canViewCost={props.canViewCost}
          codeConflict={colour && rowError(colour.id)}
          onClose={closeEditor}
          onSave={(values) => {
            const id = colour?.id ?? crypto.randomUUID();
            const next = {...values, id};
            props.update({
              ...props.draft,
              colours: colour
                ? props.draft.colours.map((row) => (row.id === id ? next : row))
                : [...props.draft.colours, next],
            });
            closeEditor();
            return Promise.resolve();
          }}
        />
      </Show>
      <Show when={editor?.kind === 'upholstery'}>
        <UpholsteryDialog
          upholstery={upholstery}
          draft={props.draft}
          canViewCost={props.canViewCost}
          onClose={closeEditor}
          onSave={(values) => {
            const id = upholstery?.id ?? crypto.randomUUID();
            const next = {...values, id};
            props.update({
              ...props.draft,
              upholsteries: upholstery
                ? props.draft.upholsteries.map((row) => (row.id === id ? next : row))
                : [...props.draft.upholsteries, next],
            });
            closeEditor();
            return Promise.resolve();
          }}
        />
      </Show>
    </VStack>
  );
}
